import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { StatusText, type StatusTone } from '@intelishs-group/ui';
import {
  severidadRegistro,
  type SeveridadRegistro,
} from '@core/complementos-bulk-tracking.service';
import type { ConteosTanda, RegistroBitacora } from '@shared/models/bitacora-masiva.model';
import {
  escribirPortapapeles,
  estatusTexto,
  referenciaCorta,
} from '@shared/utils/bulk-registros.rules';

/** Metadatos visuales por severidad de renglón. */
const SEVERIDAD_META: Record<SeveridadRegistro, { tono: StatusTone }> = {
  exito: { tono: 'ok' },
  error: { tono: 'error' },
  proceso: { tono: 'progreso' },
};

/**
 * Referencias de un grupo, colapsadas para caber en una línea.
 *
 * En una carga masiva las referencias son casi siempre contiguas, así que 600
 * renglones se resumen en un puñado de rangos. Enumerarlos uno por uno —chips o
 * texto separado por comas, da igual— produce un muro que no se escanea y que
 * empuja el bloque de errores fuera de la vista.
 */
interface ResumenReferencias {
  /** Prefijo común de todas las referencias (p. ej. `"Fila "`), o vacío. */
  prefijo: string;
  /** Tramos ya colapsados (`"1–142"`, `"144"`), o las referencias crudas. */
  tokens: string[];
}

/** Un bloque del detalle: los renglones de una severidad. */
interface GrupoRegistros {
  severidad: SeveridadRegistro;
  titulo: string;
  /** `true` para filas con estatus y mensaje; `false` para el resumen de rangos. */
  detallado: boolean;
  items: RegistroBitacora[];
  /** Referencias colapsadas. Solo se usa en los grupos no detallados. */
  resumen: ResumenReferencias;
  /**
   * Renglones de esta severidad en la tanda COMPLETA, según el resumen del
   * backend. `null` si la tanda todavía no tiene resumen.
   */
  totalTanda: number | null;
  /** `true` cuando la bitácora cargada no alcanza a cubrir {@link totalTanda}. */
  parcial: boolean;
}

/** De qué conteo del resumen sale el total de cada severidad. */
const CONTEO_TANDA: Record<SeveridadRegistro, keyof ConteosTanda> = {
  exito: 'completados',
  error: 'conError',
  proceso: 'procesando',
};

/**
 * Colapsa referencias a tramos contiguos.
 *
 * Solo aplica cuando TODAS comparten prefijo y terminan en número —el caso de
 * `"Fila 1"…`—; si alguna no encaja (referencias libres de otro cliente) se
 * devuelven crudas, que es peor pero nunca miente sobre lo que hay.
 *
 * @param referencias Referencias en el orden en que llegaron.
 * @returns Prefijo común y tramos, o las referencias tal cual.
 */
function resumirReferencias(referencias: string[]): ResumenReferencias {
  const crudas: ResumenReferencias = { prefijo: '', tokens: referencias };
  if (!referencias.length) return crudas;

  const partes = referencias.map((referencia) => /^(.*?)(\d+)$/.exec(referencia));
  if (partes.some((parte) => parte === null)) return crudas;

  const prefijo = partes[0]![1];
  if (partes.some((parte) => parte![1] !== prefijo)) return crudas;

  const numeros = [...new Set(partes.map((parte) => Number(parte![2])))].sort((a, b) => a - b);

  const tokens: string[] = [];
  let inicio = numeros[0];
  let previo = numeros[0];

  for (const numero of numeros.slice(1)) {
    if (numero === previo + 1) {
      previo = numero;
      continue;
    }
    tokens.push(inicio === previo ? `${inicio}` : `${inicio}–${previo}`);
    inicio = numero;
    previo = numero;
  }
  tokens.push(inicio === previo ? `${inicio}` : `${inicio}–${previo}`);

  return { prefijo, tokens };
}

/**
 * Orden y forma de los grupos del detalle.
 *
 * El error va primero y es el único detallado porque es lo único que pide una
 * acción del usuario: es la razón por la que abre este panel. Los timbrados y los
 * que siguen en proceso se colapsan a chips —basta con confirmar CUÁLES son— para
 * que un mensaje repetido ("Complemento timbrado correctamente") no empuje los
 * errores fuera de la zona visible.
 */
const GRUPOS: ReadonlyArray<Pick<GrupoRegistros, 'severidad' | 'titulo' | 'detallado'>> = [
  { severidad: 'error', titulo: 'Con error', detallado: true },
  { severidad: 'exito', titulo: 'Timbrados', detallado: false },
  { severidad: 'proceso', titulo: 'En proceso', detallado: false },
];

/** Tiempo que el botón de copiar se queda en estado "copiado". */
const AVISO_COPIA_MS = 1_600;

/** Tramos que se muestran antes de cortar con un "ver todos". */
const MAX_TOKENS = 12;

/**
 * Detalle renglón por renglón de una tanda masiva, agrupado por severidad.
 *
 * Es puramente presentacional: recibe los renglones por `input()` y no sabe de dónde
 * vienen. Lo montan dos dueños distintos —el panel de seguimiento, alimentado por
 * {@link ComplementosBulkTrackingService}, y el drawer del histórico de Cargas, con su
 * propio cargador— para que abrir una tanda vieja no mueva lo que el navbar sigue.
 */
@Component({
  selector: 'intelishs-bulk-registros',
  imports: [NzIconModule, StatusText],
  templateUrl: './bulk-registros.html',
  styleUrl: './bulk-registros.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkRegistrosComponent {
  /** Renglones cargados de la bitácora. */
  readonly registros = input.required<readonly RegistroBitacora[]>();
  /** Conteos de la tanda completa; `null` mientras no hay resumen. */
  readonly conteos = input<ConteosTanda | null>(null);
  /** Renglones que el backend reporta para la tanda (pueden exceder los cargados). */
  readonly total = input(0);

  /**
   * `true` cuando la bitácora trae más renglones de los que caben en una página,
   * para advertirlo en lugar de mostrar un listado incompleto en silencio.
   */
  protected readonly hayRegistrosOcultos = computed(() => this.total() > this.registros().length);

  /**
   * Renglones repartidos por severidad, en el orden de {@link GRUPOS}. Los grupos
   * vacíos no se emiten para que el panel no muestre encabezados huecos.
   *
   * Los conteos son de los renglones CARGADOS, no de la tanda: si la bitácora
   * viene paginada pueden ser menores que los del resumen, y eso lo advierte la
   * nota de `hayRegistrosOcultos`.
   */
  protected readonly grupos = computed<GrupoRegistros[]>(() => {
    const registros = this.registros();
    const resumenTanda = this.conteos();

    return GRUPOS.map((grupo) => {
      const items = registros.filter((registro) => severidadRegistro(registro) === grupo.severidad);
      const conteo = resumenTanda?.[CONTEO_TANDA[grupo.severidad]];
      const totalTanda = typeof conteo === 'number' ? conteo : null;

      return {
        ...grupo,
        items,
        resumen: grupo.detallado
          ? { prefijo: '', tokens: [] }
          : resumirReferencias(items.map((registro) => this.referenciaCorta(registro))),
        totalTanda,
        parcial: totalTanda !== null && totalTanda > items.length,
      };
      // Una zona que la tanda reporta pero cuyos renglones no vienen en la página
      // cargada SE MUESTRA IGUAL, vacía y explicándolo: los errores de una tanda
      // grande pueden quedar fuera de la página. Ocultarla haría que el detalle
      // dijera "no hay errores" mientras el resumen dice que sí los hay.
    }).filter((grupo) => grupo.items.length > 0 || (grupo.totalTanda ?? 0) > 0);
  });

  /** Severidades cuyo resumen se está mostrando completo. */
  private readonly expandidos = signal<ReadonlySet<SeveridadRegistro>>(new Set());

  /** `idx` cuyo botón de copiar está en estado "copiado". */
  protected readonly copiado = signal<string | null>(null);
  /** Texto para el lector de pantalla al copiar. */
  protected readonly avisoCopia = signal('');
  private temporizadorCopia: ReturnType<typeof setTimeout> | null = null;

  /**
   * Metadatos visuales de un renglón según su estatus.
   *
   * @param registro Renglón de bitácora.
   * @returns Tono con que se pinta.
   */
  protected meta(registro: RegistroBitacora): { tono: StatusTone } {
    return SEVERIDAD_META[severidadRegistro(registro)];
  }

  /**
   * Tramos que se pintan de un grupo: los primeros {@link MAX_TOKENS}, o todos si
   * el usuario lo expandió.
   *
   * @param grupo Grupo no detallado.
   * @returns Tramos a mostrar.
   */
  protected tokensVisibles(grupo: GrupoRegistros): string[] {
    if (this.expandidos().has(grupo.severidad)) return grupo.resumen.tokens;
    return grupo.resumen.tokens.slice(0, MAX_TOKENS);
  }

  /**
   * Tramos que quedaron fuera del corte.
   *
   * @param grupo Grupo no detallado.
   * @returns Cuántos tramos no se están mostrando.
   */
  protected tokensOcultos(grupo: GrupoRegistros): number {
    return grupo.resumen.tokens.length - this.tokensVisibles(grupo).length;
  }

  /**
   * Alterna entre el resumen cortado y la lista completa de tramos.
   *
   * @param grupo Grupo no detallado.
   */
  protected alternarResumen(grupo: GrupoRegistros): void {
    this.expandidos.update((severidades) => {
      const siguiente = new Set(severidades);
      if (!siguiente.delete(grupo.severidad)) siguiente.add(grupo.severidad);
      return siguiente;
    });
  }

  /**
   * Copia las referencias del grupo, ya expandidas y separadas por coma.
   *
   * Es la salida para lo que el resumen por rangos no puede dar: pegar la lista
   * en la hoja de cálculo desde la que se armó la carga.
   *
   * @param grupo Grupo cuyo listado se copia.
   */
  protected async copiarReferencias(grupo: GrupoRegistros): Promise<void> {
    const referencias = grupo.items.map((registro) => this.referenciaCorta(registro)).join(', ');
    const copiado = await escribirPortapapeles(referencias);
    this.marcarCopia(
      copiado ? `copia:${grupo.severidad}` : null,
      copiado
        ? `${grupo.items.length} referencias de ${grupo.titulo.toLowerCase()} copiadas`
        : 'No se pudieron copiar las referencias.',
    );
  }

  /**
   * Ancla del renglón (p. ej. `"Fila 12"`): va en la columna fija de la izquierda, en
   * neutro. El color se reserva para el estatus.
   */
  protected readonly referenciaCorta = referenciaCorta;

  /** Estatus legible del renglón. */
  protected readonly estatusTexto = estatusTexto;

  /**
   * Copia el `idx` del renglón al portapapeles.
   *
   * El `idx` es el identificador con que soporte liga el renglón con el
   * complemento timbrado: no se lee, se pasa. Por eso vive detrás de esta acción.
   *
   * @param registro Renglón de bitácora.
   */
  protected async copiarFolio(registro: RegistroBitacora): Promise<void> {
    const folio = registro.idx?.trim();
    if (!folio) return;

    const copiado = await escribirPortapapeles(folio);
    this.marcarCopia(
      copiado ? folio : null,
      copiado
        ? `Folio de ${this.referenciaCorta(registro)} copiado`
        : 'No se pudo copiar el folio.',
    );
  }

  /**
   * Deja el acuse de copia en pantalla y lo retira solo.
   *
   * @param clave Clave que queda en estado "copiado", o `null` si falló.
   * @param aviso Texto para el lector de pantalla.
   */
  private marcarCopia(clave: string | null, aviso: string): void {
    this.copiado.set(clave);
    this.avisoCopia.set(aviso);

    if (this.temporizadorCopia) clearTimeout(this.temporizadorCopia);
    this.temporizadorCopia = setTimeout(() => {
      this.copiado.set(null);
      this.avisoCopia.set('');
    }, AVISO_COPIA_MS);
  }
}
